
import { 
  Card, 
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, RefreshCw, X } from "lucide-react";

interface SwitchingCostCalculatorProps {
  option: {
    id: number;
    currentSupplier: string;
    newSupplier: string;
    category: string;
    currentCost: number;
    newCost: number;
    switchingFees: number;
    contractTerms: string;
    qualityDifference: string;
    reliabilityRisk: string;
    logisticsChange: string;
  };
}

export const SwitchingCostCalculator = ({ option }: SwitchingCostCalculatorProps) => {
  const monthlySavings = option.currentCost - option.newCost;
  const annualSavings = monthlySavings * 12;
  const breakEvenMonths = Math.ceil(option.switchingFees / monthlySavings);
  const netFirstYearSavings = annualSavings - option.switchingFees;

  const isRecommended = 
    netFirstYearSavings > 0 && 
    option.qualityDifference !== "Lower" && 
    option.reliabilityRisk !== "High";

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-muted/30 pb-3">
        <CardTitle className="text-lg flex justify-between items-center">
          <span>Switching Analysis</span>
          {isRecommended ? (
            <Badge variant="outline" className="flex items-center bg-green-100 text-green-800 border-green-300">
              <Check className="h-3 w-3 mr-1" />
              Recommended
            </Badge>
          ) : (
            <Badge variant="outline" className="flex items-center bg-red-100 text-red-800 border-red-300">
              <X className="h-3 w-3 mr-1" />
              Not Recommended
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div className="space-y-4">
          <div className="flex justify-between">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Current Supplier</p>
              <p className="font-medium">{option.currentSupplier}</p>
            </div>
            <div className="text-2xl text-muted-foreground">→</div>
            <div className="space-y-1 text-right">
              <p className="text-xs text-muted-foreground">New Supplier</p>
              <p className="font-medium">{option.newSupplier}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Current Monthly Cost</p>
              <p className="font-semibold text-red-500">${option.currentCost.toFixed(2)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">New Monthly Cost</p>
              <p className="font-semibold text-green-600">${option.newCost.toFixed(2)}</p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm">Monthly Savings</span>
              <span className="font-semibold text-green-600">${monthlySavings.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Annual Savings</span>
              <span className="font-semibold text-green-600">${annualSavings.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Switching Fees</span>
              <span className="font-semibold text-red-500">${option.switchingFees.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t pt-2">
              <span className="text-sm font-semibold">Net First Year Savings</span>
              <span className={`font-bold ${netFirstYearSavings > 0 ? 'text-green-600' : 'text-red-500'}`}>
                ${netFirstYearSavings.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm">Break-even</span>
              <Badge variant="secondary">{breakEvenMonths} months</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Quality</span>
              <Badge variant={
                option.qualityDifference === "Similar" ? "outline" : 
                option.qualityDifference === "Slightly lower" ? "secondary" : 
                "destructive"
              }>
                {option.qualityDifference}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Reliability Risk</span>
              <Badge variant={
                option.reliabilityRisk === "Low" ? "outline" :
                option.reliabilityRisk === "Medium" ? "secondary" :
                "destructive"
              }>
                {option.reliabilityRisk}
              </Badge>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="bg-muted/30 p-3">
        <Button variant="outline" size="sm" className="w-full">
          <RefreshCw className="h-4 w-4 mr-1" />
          Run Detailed Analysis
        </Button>
      </CardFooter>
    </Card>
  );
};
